import {
    booleanAttribute,
    Component,
    effect,
    ElementRef,
    input,
    linkedSignal,
    OnInit,
    output,
    viewChild,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { MatTooltipModule, TooltipPosition } from '@angular/material/tooltip';

import { selfOrParentHasClass } from '../../helpers';
import { FormControlErrors } from '../form-control-errors/form-control-errors';
import { Icon } from '../icon';
import { Tooltip } from '../tooltip';

@Component({
    selector: 'stby-checkbox',
    styleUrl: './checkbox.scss',
    templateUrl: './checkbox.html',
    host: { '[class]': 'checkboxStyle()' },
    imports: [FormControlErrors, MatTooltipModule, Tooltip, Icon],
})
export class Checkbox implements OnInit {
    readonly contentWrapper = viewChild<ElementRef>('contentWrapper');

    readonly alwaysShowErrors = input(false, { transform: booleanAttribute });
    readonly noSpacing = input(false, { transform: booleanAttribute });
    readonly spacingSmall = input(false, { transform: booleanAttribute });
    readonly errored = input(false, { transform: booleanAttribute });
    readonly onlyAllowCheckboxClick = input(false, { transform: booleanAttribute });

    readonly checkboxStyle = input('default');
    readonly label = input('');
    readonly requiredLabel = input<string>();

    readonly labelHasTabindex = input(false);
    readonly id = input<string>('');

    // Caution! Don't use controlName in modals because in modals the form group can be initialized later than formControl
    // and so isFormControl() here will return false, causing the formControl to not be updated
    readonly controlName = input<string>();
    readonly group = input<AbstractControl>();

    readonly tooltip = input<string>();
    readonly tooltipPosition = input<TooltipPosition>('above');

    // Kept undefined when not explicitly bound so the disabled-status side effect below only
    // runs for consumers that actually pass [disabled], matching the previous @Input setter behaviour.
    readonly disabledInput = input<boolean | undefined>(undefined, {
        alias: 'disabled',
        transform: (value: unknown): boolean | undefined => (value == null ? undefined : booleanAttribute(value)),
    });
    protected readonly disabled = linkedSignal(() => this.disabledInput() ?? false);

    readonly checkedInput = input(false, { alias: 'checked', transform: booleanAttribute });
    protected readonly checked = linkedSignal(() => this.checkedInput());

    readonly indeterminate = input(false, { transform: booleanAttribute });

    readonly checkedChangeCallback = input<() => void>();
    readonly checkedChange = output<boolean>();

    formControl: AbstractControl;

    constructor() {
        effect(() => {
            const disabledInput = this.disabledInput();
            if (disabledInput !== undefined) {
                this.setFormControlDisabledStatus(disabledInput);
            }
        });
    }

    ngOnInit() {
        this.populateFormControl();
        this.initFormControl();
    }

    private initFormControl() {
        const formControl = this.group()?.get(this.controlName());

        if (formControl) {
            this.disabled.set(formControl.disabled);

            formControl.statusChanges.subscribe(() => {
                this.disabled.set(formControl.disabled);
            });
        }
    }

    handleChange() {
        this.checkedChange.emit(this.checked());
    }

    setChecked(value: boolean): void {
        if (value !== this.checked()) {
            this.checked.set(value);
            this.setFormControlValue(value);
        }
    }

    toggleChecked(event: Event) {
        if (this.onlyAllowCheckboxClick() && !selfOrParentHasClass(event.target as Element, 'checkbox', true)) {
            return;
        }
        if (this.disabled() || selfOrParentHasClass(event.target as Element, 'js-deny-value-setting', true)) {
            return;
        }

        // Event emits get cancelled when stopping propagation so the preferred method is a callback function
        const checkedChangeCallback = this.checkedChangeCallback();
        if (typeof checkedChangeCallback === 'function') {
            checkedChangeCallback();
            event.stopPropagation();
        } else {
            this.setChecked(!this.checked());
            this.handleChange();
        }
    }

    private isFormControl(): boolean {
        this.populateFormControl();
        return !!this.formControl;
    }

    private populateFormControl(): void {
        const group = this.group();
        const controlName = this.controlName();
        if (!group || !controlName) {
            this.formControl = null;
            return;
        }
        this.formControl = group.get(controlName);
    }

    private setFormControlDisabledStatus(status: boolean): void {
        if (this.isFormControl()) {
            if (status) {
                this.formControl.disable();
            } else {
                this.formControl.enable();
            }
        }
    }

    private setFormControlValue(value: any): void {
        if (this.isFormControl()) {
            this.formControl.setValue(value);
        }
    }
}
